import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Button from '../common/Button';
import Modal from '../common/Modal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const PlatformSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Form state for global booking limit
  const [bookingLimitEnabled, setBookingLimitEnabled] = useState(true);
  const [limitHours, setLimitHours] = useState(15);
  const [windowDays, setWindowDays] = useState(7);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/platform-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSettings(response.data);

      // Populate form with current values
      if (response.data.global_weekly_booking_limit) {
        const limitConfig = response.data.global_weekly_booking_limit.value;
        setBookingLimitEnabled(limitConfig.enabled);
        setLimitHours((limitConfig.limit_minutes || 900) / 60);
        setWindowDays(limitConfig.window_days || 7);
      }
    } catch (err) {
      console.error('Error fetching platform settings:', err);
      setError(err.response?.data?.error || 'Failed to load platform settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBookingLimit = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage('');

      const token = localStorage.getItem('token');
      const limitMinutes = Math.round(limitHours * 60);

      await axios.put(
        `${API_URL}/api/platform-settings/global_weekly_booking_limit`,
        {
          setting_value: {
            enabled: bookingLimitEnabled,
            limit_minutes: limitMinutes,
            window_days: windowDays
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccessMessage('Global booking limit updated successfully!');
      fetchSettings(); // Refresh settings

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving booking limit:', err);
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading platform settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Platform Settings</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Configure system-wide settings and policies
                </p>
              </div>
              <Button
                onClick={() => window.history.back()}
                variant="outline"
              >
                Back
              </Button>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Global Booking Limit Settings */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Global Booking Limit</h2>
              <p className="text-sm text-gray-600">
                Prevent excessive booking across all gyms by setting a weekly limit for users
              </p>
            </div>

            <div className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-900">
                    Enable Global Booking Limit
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    When enabled, users cannot exceed the weekly booking limit across all gyms
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setBookingLimitEnabled(!bookingLimitEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
                    bookingLimitEnabled ? 'bg-teal-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      bookingLimitEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Limit Hours */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weekly Limit (Hours)
                </label>
                <input
                  type="number"
                  min="1"
                  max="168"
                  step="0.5"
                  value={limitHours}
                  onChange={(e) => setLimitHours(parseFloat(e.target.value))}
                  disabled={!bookingLimitEnabled}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Maximum hours a user can book per week across all gyms (equivalent to {Math.round(limitHours * 60)} minutes)
                </p>
              </div>

              {/* Window Days */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rolling Window (Days)
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={windowDays}
                  onChange={(e) => setWindowDays(parseInt(e.target.value))}
                  disabled={!bookingLimitEnabled}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">
                  The time window for calculating booking limits (7 days = weekly limit)
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-900">Current Configuration</h3>
                    <div className="mt-2 text-sm text-blue-700">
                      {bookingLimitEnabled ? (
                        <p>
                          Users can book a maximum of <strong>{limitHours} hours</strong> within any {windowDays}-day period across all gyms.
                        </p>
                      ) : (
                        <p>Global booking limit is currently <strong>disabled</strong>. Users can book unlimited time.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  onClick={handleSaveBookingLimit}
                  disabled={saving}
                  className="min-w-[150px]"
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </div>
          </div>

          {/* Additional Settings Placeholder */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="text-center py-8 text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              <p className="text-sm">Additional platform settings can be added here</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformSettings;
